import React, { useCallback, useMemo } from 'react';
import FormikFieldWrapper from '../general/formikFieldWrapper/formikFieldWrapper';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import QuestionValidationRuleProjections from '../../projections/questionValidationRule';
import { useFormikContext } from 'formik';
import questionProjections from '../../projections/question';
import { getQuestionFieldInputProps } from './formikQuestionInputProps';
import * as action from '../../actions';
import codes from '../../utils/constants/codes';
import {
    ObjectOfAny,
    ObjectOfStrings,
    QuestionSchema,
} from '../../reducers/types';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { CurrencySchema } from '../../actions';

type Props = {
    question: QuestionSchema;
    questionTypes: ObjectOfAny;
    formGroupContainerStyle?: React.CSSProperties;
    labelSize?: string;
    inputSize?: string;
    infoText?: string;
    isReadOnlyOrEditableQuestion?: string;
    hasInlineElements?: boolean;
    inlineGroupElements?: ObjectOfAny;
    inlineReadOnlyElement?: ObjectOfAny;
    isDisabledQuestion?: boolean;
    currency?: ObjectOfStrings | undefined;
    currencyOptions?: CurrencySchema[] | null;
    onAutoSaveDraftClaim?: (values: any) => void;
    handleCurrencySelectWrapper?: (
        currency: ObjectOfStrings | undefined,
        values?: any,
    ) => void;
};

type FormikArgsProps = {
    field: any;
    values: any;
    setFieldValue: Function;
    setValues: Function;
    handleCurrencySelectWrapper: () => {};
};

const FormikQuestion = (props: Props) => {
    const {
        question,
        questionTypes,
        currency,
        currencyOptions,
        handleCurrencySelectWrapper,
        onAutoSaveDraftClaim,
        formGroupContainerStyle,
        labelSize,
        inputSize,
        infoText,
        isReadOnlyOrEditableQuestion,
        hasInlineElements,
        inlineGroupElements,
        inlineReadOnlyElement,
        isDisabledQuestion,
    } = props;
    const { values, errors } = useFormikContext();
    const dispatch = useAppDispatch();

    const token = useAppSelector(state => state.applicationUser.accessToken);
    const questions = useAppSelector(state => state.question.question.byId);

    const questionHelpText = useAppSelector(
        questionProjections.questionHelperText(question.code, values),
    ) as string | null;
    const questionValidationRules = useAppSelector(
        QuestionValidationRuleProjections.filteredRulesByCodes(
            question.questionValidationRules,
        ),
    );
    const questionTypeCode = questionTypes[question.questionTypeId]
        ? questionTypes[question.questionTypeId].code
        : null;

    const formGroupVariant = GENERAL_FUNCTIONS.getInputItem(
        questionTypeCode,
        question,
        isReadOnlyOrEditableQuestion,
    );

    const changeAnswerStateAction = useCallback(
        (payload: any) => dispatch(action.changeAnswerState(payload)),
        [dispatch],
    );
    const isReadOnlyQuestionHandler = useMemo(() => {
        return (
            isReadOnlyOrEditableQuestion === codes.isEditable ||
            isReadOnlyOrEditableQuestion === codes.isReadOnly
        );
    }, [isReadOnlyOrEditableQuestion]);

    const getInputProps = (formikArgs: FormikArgsProps) => {
        return getQuestionFieldInputProps(formikArgs, {
            question,
            questionTypeCode,
            handleCurrencySelectWrapper,
            currency,
            currencyOptions,
            onAutoSaveDraftClaim,
            changeAnswerStateAction,
            token,
            inlineReadOnlyElement,
            formGroupVariant,
            inlineGroupElements,
            questions,
        });
    };

    return (
        <FormikFieldWrapper
            key={question.id}
            helpText={!isReadOnlyQuestionHandler ? questionHelpText : null}
            variant={formGroupVariant}
            label={question.name as string}
            validator={GENERAL_FUNCTIONS.validationCreator(question)(
                questionValidationRules,
                values,
                [],
                isReadOnlyQuestionHandler,
            )}
            getInputProps={getInputProps}
            inputProps={{ name: question.code }}
            description={
                !isReadOnlyQuestionHandler ? question.description : null
            }
            formGroupContainerStyle={formGroupContainerStyle}
            labelSize={labelSize ? labelSize : undefined}
            inputSize={inputSize ? inputSize : undefined}
            infoText={infoText}
            hasInlineElements={hasInlineElements}
            inlineGroupElements={inlineGroupElements}
        />
    );
};

export default FormikQuestion;
